import React, { ReactElement } from 'react';

import { Disclosure } from '@headlessui/react';
import classNames from 'classnames';

import styles from './Accordion.module.css';
import { Button } from '../button/Button';
import { Icon, Icons } from '../icon/Icon';

export type AccordionProps = {
  title: string | ReactElement | ReactElement[];
  titleClassName?: string;
  headerClassName?: string;
  icon?: (typeof Icons)[number];
  children?: ReactElement | ReactElement[] | string;
  variant?: 'primary' | 'secondary';
  small?: boolean;
  frame?: boolean;
  iconPosition?: 'default' | 'hug';
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>;

export const Accordion = ({
  icon,
  title,
  children,
  frame = true,
  iconPosition = 'default',
  variant = 'primary',
  small,
  className,
  titleClassName,
  headerClassName,
  ...accordionProps
}: AccordionProps) => {
  return (
    <Disclosure
      as="div"
      className={classNames(
        styles.accordion,
        {
          [styles.primary]: variant === 'primary',
          [styles.secondary]: variant === 'secondary',
          [styles.small]: small,
          [styles.frame]: frame,
        },
        className,
      )}
      {...accordionProps}
    >
      {({ open }) => (
        <>
          <Disclosure.Button
            as="div"
            className={classNames(styles.header, headerClassName, {
              [styles.iconHugHeader]: iconPosition === 'hug',
            })}
          >
            <div
              className={classNames(styles.title, titleClassName, {
                [styles.noFlexGrow]: iconPosition === 'hug',
              })}
            >
              {icon && <Icon className={styles.icon} icon={icon} />}
              {title}
            </div>
            <Button
              className={styles.toggleButton}
              iconOnly
              variant="tertiary"
              type="button"
              aria-label={
                accordionProps['aria-label'] ||
                (typeof title === 'string' ? title : undefined)
              }
            >
              <Icon icon="angleDown" />
            </Button>
          </Disclosure.Button>

          <Disclosure.Panel
            static
            className={styles.collapsable}
            aria-hidden={!open}
            as="section"
          >
            <div className={styles.panel}>{children}</div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};
